/**
 * Percent yield.
 *
 *     percent yield = actual yield ÷ theoretical yield × 100 %
 *
 * Both yields must be in the same unit (grams with grams, moles with moles). The theoretical
 * yield is what the limiting reactant could make if the reaction went to completion with
 * no losses; the actual yield is what you measured.
 */

export interface YieldResult {
  /** actual ÷ theoretical × 100. */
  percent: number;
  /** theoretical − actual, in the shared unit; negative when the actual yield is larger. */
  shortfall: number;
  band: YieldBand;
}

/**
 * - `over`: above 100 %, which a real, dry, pure product cannot exceed;
 * - `full`: 99.5 to 100 %, a quantitative yield within the precision of a balance;
 * - `partial`: below that.
 */
export type YieldBand = "over" | "full" | "partial";

const FULL_YIELD_FLOOR = 99.5;
const FULL_YIELD_CEILING = 100.5;

export function percentYield(
  actual: number,
  theoretical: number
): YieldResult | null {
  if (!(Number.isFinite(actual) && Number.isFinite(theoretical))) {
    return null;
  }
  if (!(theoretical > 0) || actual < 0) {
    return null;
  }
  const percent = (actual / theoretical) * 100;
  let band: YieldBand = "partial";
  if (percent > FULL_YIELD_CEILING) {
    band = "over";
  } else if (percent >= FULL_YIELD_FLOOR) {
    band = "full";
  }
  return { percent, shortfall: theoretical - actual, band };
}

/** The actual yield a given percent yield would correspond to. */
export function actualFromPercent(
  percent: number,
  theoretical: number
): number {
  return (percent / 100) * theoretical;
}
